import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../utils/auth';
import '../styles/ProductKnowledge.css';

const jsonHeaders = () => ({ 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() });

// The editable house-style prompt library (design §3.6). Imported once from Ask Paul,
// then owned and edited here — generation reads OUR copy, never Ask Paul live. A pair
// with no prompt falls back to a built-in default, so the library is optional tuning.
export default function AiPrompts({ isAdmin = false }) {
  const navigate = useNavigate();

  const [prompts, setPrompts] = useState([]);
  const [typeLabels, setTypeLabels] = useState({});   // code -> label (for readable pairing)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [importing, setImporting] = useState(false);
  const [drafts, setDrafts] = useState({});           // id -> {name, body} local edits
  const [adding, setAdding] = useState(null);         // {source_type, child_type, name, body}
  const [improving, setImproving] = useState({});     // id | 'new' -> {loading, result, error}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, projs] = await Promise.all([
        fetch('/api/prompts', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/config/projects', { credentials: 'include' }).then(r => r.json()),
      ]);
      setPrompts(pr.prompts || []);
      const labels = {};
      for (const p of (projs.projects || [])) {
        for (const it of (p.item_types || [])) if (it.code) labels[it.code] = it.label || it.code;
      }
      setTypeLabels(labels);
      setError(null);
    } catch (e) {
      setError('Could not load prompts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const typeCodes = useMemo(() => Object.keys(typeLabels).sort(), [typeLabels]);
  const labelFor = useCallback(code => typeLabels[code] || code, [typeLabels]);

  const runImport = async () => {
    setImporting(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch('/api/prompts/import', {
        method: 'POST', credentials: 'include', headers: jsonHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      setNotice(`Imported from Ask Paul: ${data.created} new, ${data.updated} updated, `
        + `${data.skipped} kept (already edited), ${data.unresolved} could not be paired.`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setImporting(false);
    }
  };

  const editVal = (p, field) => (drafts[p.id]?.[field] ?? p[field]) || '';
  const setEdit = (id, field, value) =>
    setDrafts(d => ({ ...d, [id]: { ...d[id], [field]: value } }));
  const isDirty = (p) => drafts[p.id] &&
    ((drafts[p.id].name ?? p.name) !== p.name || (drafts[p.id].body ?? p.body) !== p.body);

  const savePrompt = async (p) => {
    const body = { name: editVal(p, 'name'), body: editVal(p, 'body') };
    const res = await fetch(`/api/prompts/${p.id}`, {
      method: 'PUT', credentials: 'include', headers: jsonHeaders(), body: JSON.stringify(body),
    });
    if (res.ok) {
      setDrafts(d => { const n = { ...d }; delete n[p.id]; return n; });
      load();
    } else {
      setError('Save failed');
    }
  };

  const deletePrompt = async (p) => {
    if (!window.confirm(`Delete the prompt for ${labelFor(p.source_type)} → ${labelFor(p.child_type)}?`)) return;
    await fetch(`/api/prompts/${p.id}`, {
      method: 'DELETE', credentials: 'include', headers: { 'X-CSRF-Token': getCsrfToken() },
    });
    load();
  };

  // 🪄 Improve: send the text AS ON SCREEN (may be unsaved) and show the suggestion under
  // it. Nothing is saved — "Use suggestion" only replaces the draft; Save stays explicit.
  const runImprove = async (key, body, sourceType, childType) => {
    setImproving(m => ({ ...m, [key]: { loading: true } }));
    try {
      const res = await fetch('/api/prompts/improve', {
        method: 'POST', credentials: 'include', headers: jsonHeaders(),
        body: JSON.stringify({ body, source_type: sourceType, child_type: childType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not improve the prompt');
      setImproving(m => ({ ...m, [key]: { result: data } }));
    } catch (e) {
      setImproving(m => ({ ...m, [key]: { error: e.message } }));
    }
  };
  const closeImprove = (key) => setImproving(m => { const n = { ...m }; delete n[key]; return n; });
  const applySuggestion = (key) => {
    const body = improving[key]?.result?.body;
    if (!body) return;
    if (key === 'new') setAdding(a => ({ ...a, body }));
    else setEdit(key, 'body', body);
    closeImprove(key);
  };

  const improveButton = (key, body, sourceType, childType) => isAdmin && (
    <button className="pk-improve-btn" title="Improve this prompt with AI"
      disabled={!body.trim() || improving[key]?.loading}
      onClick={() => runImprove(key, body, sourceType, childType)}>
      {improving[key]?.loading ? '⏳' : '🪄'}
    </button>
  );

  const improvePanel = (key) => {
    const st = improving[key];
    if (!st || st.loading) return null;
    if (st.error) {
      return (
        <div className="pk-improve">
          <span className="pk-improve-warn">{st.error}</span>
          <div className="pk-prompt-actions"><button className="pk-btn" onClick={() => closeImprove(key)}>Close</button></div>
        </div>
      );
    }
    const r = st.result;
    return (
      <div className="pk-improve">
        <span className="pk-improve-title">🪄 Suggested prompt</span>
        <textarea className="pk-textarea" rows={8} value={r.body} readOnly />
        {r.changes?.length > 0 && (
          <ul className="pk-improve-changes">{r.changes.map((c, i) => <li key={i}>{c}</li>)}</ul>
        )}
        {r.lost_placeholders?.length > 0 && (
          <span className="pk-improve-warn">
            ⚠ The suggestion dropped {r.lost_placeholders.map(p => `[*${p}*]`).join(', ')} — that value will
            no longer be filled in. Add it back after using the suggestion if you need it.
          </span>
        )}
        <div className="pk-prompt-actions">
          <button className="pk-btn" onClick={() => closeImprove(key)}>Discard</button>
          <button className="pk-btn pk-btn-primary" onClick={() => applySuggestion(key)}>Use suggestion</button>
        </div>
      </div>
    );
  };

  const saveNew = async () => {
    if (!adding.source_type || !adding.child_type || !adding.body.trim()) {
      setError('Pick both types and write the prompt body.');
      return;
    }
    const res = await fetch('/api/prompts', {
      method: 'POST', credentials: 'include', headers: jsonHeaders(), body: JSON.stringify(adding),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.detail || 'Could not add prompt'); return; }
    setAdding(null);
    load();
  };

  return (
    <div className="pk-page">
      <div className="pk-header">
        <button className="pk-back" onClick={() => navigate('/trace/knowledge')}>← Products</button>
        <h1 className="pk-title">✨ AI prompts</h1>
        <div className="pk-header-actions">
          {isAdmin && (
            <>
              <button className="pk-btn" onClick={runImport} disabled={importing}>
                {importing ? 'Importing…' : 'Import from Ask Paul'}
              </button>
              <button className="pk-btn pk-btn-primary" onClick={() => setAdding({ source_type: '', child_type: '', name: '', body: '' })}>
                + New prompt
              </button>
            </>
          )}
        </div>
      </div>

      <p className="pk-intro">
        These prompts tune how generated items read — voice, structure, granularity — for each
        source→target pairing. They're your editable copy; generation never calls Ask Paul.
        A pairing without a prompt uses a sensible built-in default.
      </p>
      <p className="pk-placeholders">
        Placeholders are filled from the item you trace from: <code>[*Name*]</code>,{' '}
        <code>[*Description*]</code>, or any field's title as on its form, e.g.{' '}
        <code>[*Risk Control*]</code>. Every field of that item is also sent to the AI, with the
        product knowledge. 🪄 suggests a better wording — nothing changes until you use it and save.
      </p>

      {!isAdmin && (
        <div className="pk-readonly">
          👁 View only — AI prompts are managed by an Orcanos administrator. You can read them,
          but can't import, add, edit, or delete.
        </div>
      )}

      {error && <div className="pk-error">{error}</div>}
      {notice && <div className="pk-notice">{notice}</div>}

      {loading ? (
        <div className="pk-loading">Loading…</div>
      ) : prompts.length === 0 && !adding ? (
        <div className="pk-empty">
          {isAdmin
            ? <>No prompts yet. Click <strong>Import from Ask Paul</strong> to bring in your tenant's templates, or add one by hand.</>
            : 'No prompts yet. An Orcanos administrator can import or add them.'}
        </div>
      ) : (
        <div className="pk-prompts">
          {adding && (
            <div className="pk-prompt pk-prompt-new">
              <div className="pk-prompt-head">
                <select className="pk-input pk-prompt-type" value={adding.source_type} onChange={e => setAdding(a => ({ ...a, source_type: e.target.value }))}>
                  <option value="">Source type…</option>
                  {typeCodes.map(c => <option key={c} value={c}>{labelFor(c)}</option>)}
                </select>
                <span className="pk-arrow">→</span>
                <select className="pk-input pk-prompt-type" value={adding.child_type} onChange={e => setAdding(a => ({ ...a, child_type: e.target.value }))}>
                  <option value="">Target type…</option>
                  {typeCodes.map(c => <option key={c} value={c}>{labelFor(c)}</option>)}
                </select>
                {improveButton('new', adding.body, adding.source_type, adding.child_type)}
              </div>
              <input className="pk-input" placeholder="Prompt name (optional)" value={adding.name} onChange={e => setAdding(a => ({ ...a, name: e.target.value }))} />
              <textarea className="pk-textarea" rows={4} placeholder="How should the AI write these items?" value={adding.body} onChange={e => setAdding(a => ({ ...a, body: e.target.value }))} />
              {improvePanel('new')}
              <div className="pk-prompt-actions">
                <button className="pk-btn" onClick={() => setAdding(null)}>Cancel</button>
                <button className="pk-btn pk-btn-primary" onClick={saveNew}>Add prompt</button>
              </div>
            </div>
          )}

          {prompts.map(p => (
            <div key={p.id} className="pk-prompt">
              <div className="pk-prompt-head">
                <span className="pk-pair">
                  <strong>{labelFor(p.source_type)}</strong>
                  <span className="pk-arrow">→</span>
                  <strong>{labelFor(p.child_type)}</strong>
                </span>
                {p.imported_from && <span className="pk-chip pk-chip-imported">imported</span>}
                {improveButton(p.id, editVal(p, 'body'), p.source_type, p.child_type)}
                {isAdmin && (
                  <button className="pk-source-del" title="Delete" onClick={() => deletePrompt(p)}>×</button>
                )}
              </div>
              <input className="pk-input" placeholder="Prompt name" value={editVal(p, 'name')} readOnly={!isAdmin} onChange={e => setEdit(p.id, 'name', e.target.value)} />
              <textarea className="pk-textarea" rows={4} value={editVal(p, 'body')} readOnly={!isAdmin} onChange={e => setEdit(p.id, 'body', e.target.value)} />
              {improvePanel(p.id)}
              <div className="pk-prompt-actions">
                <span className="pk-muted">{p.updated_by ? `Edited by ${p.updated_by}` : ''}</span>
                {isAdmin && (
                  <button className="pk-btn pk-btn-primary" onClick={() => savePrompt(p)} disabled={!isDirty(p)}>
                    {isDirty(p) ? 'Save' : 'Saved'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
